import React from 'react';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import Checkbox from 'components/layout/forms/Checkbox';
import getNextObjectKey from 'services/numbers/getNextObjectKey';
import InputLabel from 'components/layout/forms/InputLabel';
import LinkButton from 'components/layout/buttons/LinkButton';
import translations from 'services/translations/translations';
import { moveItem } from 'services/arrays/moveItem';

import { useCurrency } from 'services/offers/currency';
import getDefaultDetail from './getDefaultDetail';
import priceMaterialFromDetails, {
  startPricingFromDetails,
} from './priceMaterialFromDetails';
import IDetail from '../../IDetail';
import ISubproduct from '../../ISubproduct';
import SubproductDetail from './SubproductDetail';
import RowOrder from '../RowOrder';

const SubproductDetails = ({
  collapsed,
  locked,
  onRemoveDetail,
  reordering,
  setSubproduct,
  subproduct,
  subproductQuantity,
  targetMarginPercent,
}: {
  collapsed: boolean;
  locked: boolean;
  // The deleted detail is kept by the calculation window so it can be restored
  onRemoveDetail: (detail: IDetail, index: number) => void;
  // Shows a number in front of each detail for changing their order
  reordering: boolean;
  setSubproduct: (subproduct: ISubproduct) => void;
  subproduct: ISubproduct;
  subproductQuantity: number;
  // New details follow the target margin % of the window
  targetMarginPercent: string;
}): JSX.Element | null => {
  const { t } = useTranslation([translations.common, translations.offers]);
  const currency = useCurrency();

  if (!collapsed) {
    return null;
  }

  // The material is priced again from its details (see priceMaterialFromDetails)
  const setSubproductAndCalculatePrices = (
    updatedSubproduct: ISubproduct,
    updatedField?: string
  ) => setSubproduct(priceMaterialFromDetails(updatedSubproduct, updatedField));

  // Ticking calculates the price from the details; unticking keeps the price as it is
  const handleCalculatePriceFromDetailsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Priced per unit from the details from now on; the row keeps its product or
      // free text (the + of the row)
      setSubproduct(startPricingFromDetails(subproduct));
      return;
    }

    setSubproduct({ ...subproduct, calculatePriceFromDetails: false });
  };

  const addDetail = () => {
    const key = getNextObjectKey(subproduct.details, 'key');
    setSubproduct({
      ...subproduct,
      details: [
        ...subproduct.details,
        { ...getDefaultDetail(key), followsTargetMargin: true },
      ],
    });
  };

  // The material is priced again from its details; the field changed tells which price
  // of a material priced the earlier way changes
  const setDetail =
    (detailKey: number) => (updatedField: string) => (newDetail: IDetail) => {
      const details = subproduct.details.map((detail) =>
        detail.key === detailKey ? newDetail : detail
      );

      if (subproduct.calculatePriceFromDetails) {
        setSubproductAndCalculatePrices(
          { ...subproduct, details },
          updatedField
        );

        return;
      }

      setSubproduct({
        ...subproduct,
        details,
      });
    };

  const removeDetail = (detailKey: number) => () => {
    const index = subproduct.details.findIndex(
      (detail) => detail.key === detailKey
    );
    onRemoveDetail(subproduct.details[index], index);

    const updated = {
      ...subproduct,
      details: subproduct.details.filter((detail) => detail.key !== detailKey),
    };

    // A price the user has given to the material is kept
    if (subproduct.calculatePriceFromDetails) {
      setSubproductAndCalculatePrices(updated);
      return;
    }

    setSubproduct(updated);
  };

  // The details move only within their subproduct; the order does not change the prices
  const moveDetail = (fromIndex: number, toIndex: number) =>
    setSubproduct({
      ...subproduct,
      details: moveItem(subproduct.details, fromIndex, toIndex),
    });

  const offerIsLocked = () => locked;

  const SMALL_COLUMN_CLASS = 'small-column';

  return (
    <div className="item-details-box">
      {/* Free text is chosen with the + of the row, the sub rows open with its ↳ */}
      <Checkbox
        checked={subproduct.calculatePriceFromDetails}
        className="item-details-checkbox"
        disabled={offerIsLocked()}
        label={t(
          `${translations.calculationWindow}:Calculate price from details`
        )}
        onChange={handleCalculatePriceFromDetailsChange}
      />
      <>
        <RowOrder count={subproduct.details.length} reordering={reordering}>
          {/* The columns of the material rows, so the details are under them */}
          <Row className="subproduct-row detail-row">
            <Col>
              <InputLabel>{t(`${translations.common}:Category`)}</InputLabel>
            </Col>
            <Col>
              <InputLabel>
                {t(`${translations.calculationWindow}:Detail`)}
              </InputLabel>
            </Col>
            <Col className="icon-column material-actions" />
            <Col>
              <InputLabel>{t(`${translations.common}:Quantity`)}</InputLabel>
            </Col>
            <Col>
              <InputLabel>{t(`${translations.offers}:Purchase`)}</InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>{t(`${translations.offers}:Waste %`)}</InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>{t(`${translations.offers}:Expense`)}</InputLabel>
            </Col>
            <Col>
              <InputLabel>
                {t(`${translations.offers}:Selling price`)}
              </InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS} />
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>
                {`${t(
                  `${translations.offers}:Contribution margin`
                )} ${currency}`}
              </InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>
                {t(`${translations.calculationWindow}:Expense short`)}{' '}
                {t(`${translations.common}:total-short`)}
              </InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>
                {t(`${translations.calculationWindow}:Selling short`)}{' '}
                {t(`${translations.common}:total-short`)}
              </InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>
                {`${t(`${translations.offers}:Contribution margin`)} ${t(
                  `${translations.common}:total-short`
                )} ${currency}`}
              </InputLabel>
            </Col>
            <Col className={SMALL_COLUMN_CLASS}>
              <InputLabel>
                {`${t(`${translations.offers}:Contribution margin`)} %`}
              </InputLabel>
            </Col>
            <Col className="icon-column" />
          </Row>
        </RowOrder>
        {subproduct.details.map((detail, index) => (
          <RowOrder
            count={subproduct.details.length}
            index={index}
            key={`subproduct-${subproduct.key}-detail-row-${detail.key}`}
            move={moveDetail}
            reordering={reordering}
          >
            <SubproductDetail
              detail={detail}
              locked={locked}
              removeDetail={removeDetail(detail.key)}
              setDetail={setDetail(detail.key)}
              subproductQuantity={subproductQuantity}
              targetMarginPercent={targetMarginPercent}
            />
          </RowOrder>
        ))}
        {offerIsLocked() ? null : (
          <LinkButton onClick={addDetail}>
            {`+ ${t(`${translations.common}:Add row`)}`}
          </LinkButton>
        )}
      </>
    </div>
  );
};

export default SubproductDetails;
